import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Radar, FileText, Wrench, Rocket, HeartPulse, Share2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const BG = "https://images.unsplash.com/photo-1779153304203-e0d044f63e53?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxzZXJ2ZXIlMjByYWNrJTIwZGFyayUyMG1vZGVybnxlbnwwfHx8fDE3ODMwMzEyNDl8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (!loading && user) navigate("/dashboard");
  }, [user, loading, navigate]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const features = [
    { icon: Radar, title: "Free Website Scan", desc: "Instant health score across security, SEO, performance, mobile & privacy" },
    { icon: FileText, title: "AI Report + Build Brief", desc: "Every issue, the exact step-by-step fixes and a buildable AI plan — $29" },
    { icon: Wrench, title: "AI Guided Fix", desc: "Prefer it done for you? We implement the fixes with live progress — from $99" },
    { icon: Rocket, title: "Move to Emergent", desc: "We migrate your site, domain, DNS & email with zero downtime" },
    { icon: HeartPulse, title: "Pro Monitoring", desc: "Nightly re-scans, change alerts & the weekly digest — $19.99/mo" },
    { icon: Share2, title: "Shareable Reports", desc: "Downloadable PDF + private read-only links to share with your team" },
  ];

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0A0A0A]">
      {/* Left: brand + features */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/80" />
        <div className="relative flex items-center gap-3">
          <img src="/brand-logo-new.png" alt="Link Website Pro" className="h-16 w-auto object-contain bg-white rounded-xl px-3 py-2 shadow-lg" />
        </div>
        <div className="relative">
          <h1 className="font-heading text-4xl xl:text-5xl font-bold tracking-tighter text-white leading-tight">
            Scan it. Fix it.<br /><span className="brand-gradient">Move it to Emergent.</span>
          </h1>
          <p className="text-zinc-400 mt-4 max-w-md leading-relaxed">
            Scan any website for security, SEO, performance and privacy issues — then get an AI report
            with the exact fixes, have us implement them for you, or move and manage the whole thing on Emergent.
          </p>
          <div className="grid grid-cols-2 gap-4 mt-8 max-w-lg">
            {features.map((f) => (
              <div key={f.title} className="bg-white/5 border border-white/10 rounded-lg p-4 backdrop-blur-sm">
                <f.icon size={18} className="text-[#4da3ff]" />
                <p className="text-white text-sm font-medium mt-2">{f.title}</p>
                <p className="text-zinc-500 text-xs mt-1 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="relative font-mono text-xs text-zinc-600">Scan · Fix · Move · Monitor</div>
      </div>

      {/* Right: login */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex flex-col items-center mb-8">
            <img src="/brand-logo-new.png" alt="Link Website Pro" className="w-64 max-w-[82%] object-contain bg-white rounded-2xl px-4 py-3 shadow-lg" />
          </div>
          <h2 className="font-heading text-2xl font-semibold text-white tracking-tight">Welcome back</h2>
          <p className="text-zinc-400 text-sm mt-2">Sign in to scan, fix and manage your websites.</p>

          {/* Mobile capability list (left panel is hidden on small screens) */}
          <ul className="lg:hidden mt-5 space-y-2" data-testid="login-capabilities-mobile">
            {features.map((f) => (
              <li key={f.title} className="flex items-start gap-2.5">
                <f.icon size={15} className="text-[#4da3ff] mt-0.5 shrink-0" />
                <span className="text-zinc-300 text-sm">{f.title}</span>
              </li>
            ))}
          </ul>

          <button
            data-testid="google-login-btn"
            onClick={handleLogin}
            className="mt-8 w-full flex items-center justify-center gap-3 bg-white hover:bg-zinc-100 text-black rounded-lg px-4 py-3 font-medium transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            Continue with Google
          </button>

          <p className="text-zinc-600 text-xs mt-6 leading-relaxed">
            By continuing you agree to run automated, read-only scans against the domains you submit, and to our{" "}
            <a href="/legal/terms.html" className="underline hover:text-zinc-400">Terms</a>,{" "}
            <a href="/legal/privacy.html" className="underline hover:text-zinc-400">Privacy Policy</a> and{" "}
            <a href="/legal/acceptable-use.html" className="underline hover:text-zinc-400">Acceptable Use Policy</a>.
          </p>
          <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-zinc-700" data-testid="login-legal-links">
            <a href="/legal/" className="hover:text-zinc-400">Legal Center</a>
            <a href="/legal/security.html" className="hover:text-zinc-400">Security</a>
            <a href="/legal/sla.html" className="hover:text-zinc-400">Pricing &amp; SLA</a>
            <a href="/legal/api-docs.html" className="hover:text-zinc-400">API Docs</a>
          </div>
        </div>
      </div>
    </div>
  );
}
